import { ValidatorFn } from '@angular/forms';
import { AutocompleteWithControlsConfig } from '@autocomplete-with-controls';
import { PayerFeesCustomsDictionary } from '@international-invoice';
import { AddressType } from '@models';
import { CountrySettings } from '../../components/invoice-form/containers/international-invoice/components/international-invoice.interfaces';
import { StoredTranslate } from '../../components/invoice-form/containers/international-invoice/international-invoice.component';
import { ApiResponse } from '../../core/interfaces/api-interfaces';
import { InternationalInvApiService } from '../../services/international-inv-api.service';

export interface GetPromocodeResponseData {
  Promocode: string;
  DateOfEnding: string | Date;
  Note: string;
  Auto: '0' | '1';
}

export interface GetPromocodeResponse extends ApiResponse {
  data: GetPromocodeResponseData[];
}

export interface GetPromocodeParams {
  Phone: string;
  ShowAutoPromocodes: boolean;
}

export interface GetCountriesData {
  CallingCode: string;
  CustomsFeesActive: string; // Actually is a number string
  DeclaredCost: string; // Actually is a number string
  Code: string;
  DefaultZipCode: string;
  Description: string;
  // API changed DescriptionEn to DescriptionEng without notice
  DescriptionEn?: string;
  DescriptionEng?: string;
  DescriptionRu: string;
  Ref: string;
  CityTooltip?: string;
  PhonePrefix?: string;
  PhoneMask?: (string | RegExp)[];
  PhoneValidators?: ValidatorFn[];
  PhoneErrors?: Record<string, string>;
  RecipientPostboxAvailable?: boolean;
  RecipientSpotSelectionAvailable?: boolean;
  RecipientRegistrationAddressRequired?: boolean;
  DisplayRecipientRegistrationAddressFor?: AddressType[];
  Settings: CountrySettings[];
  State?: {
    config: AutocompleteWithControlsConfig;
    getMethodName: keyof InternationalInvApiService;
    errorSnackMessages: Record<string, string>;
  };
  Index?: {
    validators: ValidatorFn[];
  };
  AllowedWarehouseOwners?: string[];
  CodeUKTZED?: {
    validators?: ValidatorFn[];
    resetFieldsWhenSwitchedToThisCountry?: boolean;
    maxLength?: number;
  };
  CodeUKTZEDDest?: {
    validators?: ValidatorFn[];
    minLength?: number;
  };
  RecipientPayerAllowed?: boolean;
  RecipientPayerAllowedOverride?: boolean;
}

export interface CountryState {
  Code: string;
  Description: string;
  DescriptionEng: string;
}

export type WarehouseCategory = 'Poshtomat' | 'Warehouse';

export enum IICargoTypeRef {
  DOCUMENTS = 'b12b3781-70d6-11e8-9296-0025b502a04e',
  CARGO = 'b12ae488-70d6-11e8-9296-0025b502a04e',
  PARCEL = '41f7a34b-c684-11ee-bd0a-005056bd05d0',
}

export interface DeliveryType {
  Code: string;
  Description: string;
  DescriptionEng: string;
  IsActive: string;
  Ref: string;
}

export type DeliveryTypeExtended = DeliveryType & CountrySettings;

export interface GetCitiesData {
  City: string;
  CountryRef: string;
  Description: string;
  WarehouseCategories: Array<WarehouseCategory>;
  WarehouseOwners: string[];
  ZipCode: string;
  HavePoshtomats: boolean;
  HaveWarehouses: boolean;
}

export interface GetGoodsClassifierData {
  NoExportFrom: boolean;
  NoImportTo: boolean;
  HsCodeDept: string;
  HsCodeDest: string;
  Id: string;
  ProductDescription: string;
  ProductDescriptionEn: string;
  CategoryDescription: string;
  CategoryDescriptionEn: string;
  SubCategoryDescription: string;
  SubCategoryDescriptionEn: string;
}

export interface InvoiceForApi {
  translatedItems: StoredTranslate;
  EoriCodeSender?: string;
  EoriCodeRecipient?: string;
  OrderNumber?: string;

  [key: string]: any; // Allows any other properties
}

export interface GetWarehousesData {
  CountryCode: string;
  CountryDescription: string;
  CountryRef: string;
  FullDescription: string;
  WarehouseArea: string;
  WarehouseBuilding: string;
  WarehouseCity: string;
  WarehouseClosingDate: string;
  WarehouseCode: string;
  WarehouseDescription: string;
  WarehouseLanguage: string;
  WarehouseLatitude: string;
  WarehouseLongitude: string;
  WarehouseNumber: string;
  WarehouseOpeningDate: string;
  WarehouseOwner: string;
  WarehouseRef: string;
  WarehouseRegion: string;
  WarehouseStreet: string;
  WarehouseZipCode: string;
}

export interface GetLightReturnReasonsData {
  isPossibilityLightReturn: boolean;
  reasonLightReturn: string;
  subReasonsLightReturn: GetLightReturnReason[];
}

export interface GetLightReturnReason {
  Description: string;
  Ref: string;
  ReasonRef: string;
}

export interface GetLightReturnReasonsResponse extends ApiResponse {
  data: GetLightReturnReasonsData[];
}

export interface LightReturnData {
  Ref: string;
  Number: string;
}

export interface LightReturnResponse extends ApiResponse {
  data: LightReturnData[];
}

export interface GetCurrencyRatesData {
  Code: string;
  CurrencyAlfa3: string;
  Date: string;
  Name: string;
  Rate: string;
}

export interface GetCurrenciesData {
  Code: string;
  Description: string;
  DescriptionEng: string;
  IsActive: string;
  Ref: string;
}

export interface CreateInternationalInvoiceResponse {
  Ref: string,
  InternalRef: string,
  Number: string,
  Cost: number,
  State: string,
  DocumentCost: InternationalInvoiceDocumentCost,
}

export interface CreateInternationalInvoiceTemplateResponse extends CreateInternationalInvoiceResponse {}

export interface UpdateInternationalInvoiceResponse extends CreateInternationalInvoiceResponse {
  ref?: string;
}

export interface InternationalInvoiceDocumentCost {
  DomesticCurrencyCost: number,
  DomesticCurrencyTariff: number,
  DomesticCurrencyRebate: number,
  PercentDiscount: number,
  CurrencyCode: string,
}

export interface GetIncotermsData {
  Code: string;
  Description: string;
  DescriptionEng: string;
  IsActive: string;
  Ref: string;
}

export interface GetDocumentPriceByCounterpartyTypeService {
  ServiceId: string;
  ServiceName: string;
  Amount: number;
  Price: number;
  Cost: number;
  Discount: number;
}

export interface GetDocumentPriceByCounterpartyTypePayloadParcel {
  Width: number,
  Length: number,
  Height: number,
  ActualWeight: number,
}

export interface GetDocumentPriceByCounterpartyTypePayload {
  CounterpartyType: 'PrivatePerson' | 'Organization',
  PickupCountry: string,
  DeliveryCountry: string,
  ServiceType: string,
  DeliveryType: string,
  ShipmentType: string,
  PaymentMethod: string,
  PayerType: string,
  PayerFeesCustoms: typeof PayerFeesCustomsDictionary[keyof typeof PayerFeesCustomsDictionary],
  Incoterm: string,
  Cost: number,
  Currency: string,
  Parcels: GetDocumentPriceByCounterpartyTypePayloadParcel[],
  SenderEDRPOU?: string,
}

export interface GetDocumentPriceByCounterpartyTypeData {
  Cost: number;
  DomesticCurrencyCost: number;
  DomesticCurrencyTariff: number;
  DomesticCurrencyRebate: number;
  PercentDiscount: number;
  CurrencyCode: string;
  Services: GetDocumentPriceByCounterpartyTypeService[];
}
